import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Clock, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight,
  Send, Flag, Eye, X, Loader, BookOpen,
} from "lucide-react";
import {
  oeGetExamInfo, oeStartExam, oeSaveAnswer,
  oeSubmitExam, oeGetResult, oeLogViolation,
} from "../api/onlineExamApi";
import "../lms.css";

const getStudentData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; }
};

const fmtTime = (sec) => {
  if (sec <= 0) return "00:00";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/* ─── Status badge ─── */
const StatusBadge = ({ status }) => {
  const map = {
    submitted: { bg: "#dcfce7", color: "#166534", label: "Submitted" },
    graded:    { bg: "#dbeafe", color: "#1e40af", label: "Graded" },
    expired:   { bg: "#fee2e2", color: "#991b1b", label: "Expired" },
    in_progress: { bg: "#fef9c3", color: "#854d0e", label: "In Progress" },
  };
  const s = map[status] || { bg: "#f3f4f6", color: "#374151", label: status };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

/* ─── LOBBY ─── */
function ExamLobby({ info, onStart, starting }) {
  const exam          = info || {};
  const attempt       = info?.attempt || null;
  const question_count = info?.question_count || 0;
  const alreadyDone = attempt && (attempt.status === "submitted" || attempt.status === "graded");
  const inProgress  = attempt && attempt.status === "in_progress";

  const examWindowStatus = (() => {
    if (!exam.exam_date || !exam.start_time) return "open";
    const d = new Date(exam.exam_date);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const examStart = new Date(`${dateStr}T${exam.start_time}`);
    const now       = new Date();
    if (now < examStart) return "not_started";
    if (exam.duration_minutes) {
      const examEnd = new Date(examStart.getTime() + exam.duration_minutes * 60 * 1000);
      if (now > examEnd) return "ended";
    }
    return "open";
  })();

  return (
    <div style={{ maxWidth: 640, margin: "60px auto", padding: "0 16px" }}>
      <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e5e7eb", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        <div style={{ height: 5, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />
        <div style={{ padding: "32px 32px 28px" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 24 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#4f46e5,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <BookOpen size={24} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 20, color: "#111827", lineHeight: 1.3 }}>{exam.name}</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                {exam.class_name}{exam.subject_name ? ` · ${exam.subject_name}` : ""}
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "Questions", value: question_count || "—" },
              { label: "Total Marks", value: exam.total_marks || "—" },
              { label: "Duration", value: exam.duration_minutes ? `${exam.duration_minutes} min` : "—" },
              { label: "Passing Marks", value: exam.passing_marks || "—" },
              { label: "Negative Marking", value: exam.negative_marks > 0 ? `${exam.negative_marks} per wrong` : "None" },
              { label: "Exam Type", value: (exam.exam_type || "").replace("_", " ").toUpperCase() },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Instructions */}
          {exam.instructions && (
            <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, padding: "14px 16px", marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>INSTRUCTIONS</div>
              <div style={{ fontSize: 13, color: "#78350f", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{exam.instructions}</div>
            </div>
          )}

          {/* Attempt status */}
          {attempt && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8fafc", borderRadius: 10, padding: "12px 16px", marginBottom: 24, border: "1px solid #e5e7eb" }}>
              <StatusBadge status={attempt.status} />
              {alreadyDone && (
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  Score: <strong>{attempt.score}</strong> / {attempt.total_marks} · {attempt.percentage}% · <strong style={{ color: attempt.pass_fail === "pass" ? "#16a34a" : "#dc2626" }}>{attempt.pass_fail?.toUpperCase()}</strong>
                </span>
              )}
            </div>
          )}

          {/* Rules */}
          <div style={{ background: "#fef2f2", borderRadius: 10, padding: "12px 16px", marginBottom: 24, border: "1.5px solid #fecaca" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>IMPORTANT RULES</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#7f1d1d", lineHeight: 1.8 }}>
              <li>Do not switch tabs or windows during the exam</li>
              <li>Each tab switch will be recorded</li>
              <li>The exam will auto-submit when time expires</li>
              <li>You cannot re-attempt once submitted</li>
            </ul>
          </div>

          {/* Action button */}
          {alreadyDone ? (
            <div style={{ textAlign: "center", fontSize: 14, color: "#6b7280", padding: "12px 0" }}>
              You have already submitted this exam.
            </div>
          ) : examWindowStatus === "ended" ? (
            <div style={{ textAlign: "center", padding: "14px 0", background: "#fef2f2", borderRadius: 10, border: "1.5px solid #fecaca" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#991b1b" }}>Exam Window Has Closed</div>
              <div style={{ fontSize: 13, color: "#b91c1c", marginTop: 4 }}>This exam is no longer available.</div>
            </div>
          ) : examWindowStatus === "not_started" ? (
            <div style={{ textAlign: "center", padding: "14px 0", background: "#fffbeb", borderRadius: 10, border: "1.5px solid #fde68a" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#92400e" }}>Exam Not Started Yet</div>
              <div style={{ fontSize: 13, color: "#b45309", marginTop: 4 }}>
                Starts at <strong>{exam.start_time}</strong> on <strong>{new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</strong>
              </div>
            </div>
          ) : (
            <button
              onClick={onStart}
              disabled={starting}
              style={{
                width: "100%", padding: "14px 0", borderRadius: 10, border: "none",
                background: starting ? "#a5b4fc" : "linear-gradient(135deg,#4f46e5,#a855f7)",
                color: "#fff", fontSize: 15, fontWeight: 800, cursor: starting ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {starting ? <Loader size={16} className="ms2-spin" /> : null}
              {inProgress ? "Resume Exam" : "Start Exam"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── EXAM PLAYER ─── */
function ExamPlayer({ attempt, exam, paper, savedAnswers, onSubmitted }) {
  const [answers,     setAnswers]     = useState(() => {
    const map = {};
    (savedAnswers || []).forEach(a => { map[a.question_id] = a; });
    return map;
  });
  const [currentIdx,  setCurrentIdx]  = useState(0);
  const [flagged,     setFlagged]     = useState({});
  const [timeLeft,    setTimeLeft]    = useState(() => {
    const startedAt  = new Date(attempt.started_at).getTime();
    const totalSec   = (exam.duration_minutes || 60) * 60;
    const elapsed    = Math.floor((Date.now() - startedAt) / 1000);
    return Math.max(0, totalSec - elapsed);
  });
  const [submitting,  setSubmitting]  = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const autoSaveRef = useRef(null);
  const pendingSave = useRef({});

  // Flatten all questions
  const allQuestions = (paper?.sections || []).flatMap(sec =>
    (sec.questions || []).map(q => ({ ...q, question_type: sec.question_type, section_label: sec.section_label }))
  );
  const currentQ = allQuestions[currentIdx];

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) { handleAutoSubmit(); return; }
    const t = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  // Tab switch detection
  useEffect(() => {
    const onBlur = () => {
      oeLogViolation(attempt.id).catch(() => {});
      toast.warn("Tab switch detected and recorded!", { autoClose: 3000 });
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [attempt.id]);

  // Auto-save every 30s
  useEffect(() => {
    autoSaveRef.current = setInterval(() => {
      Object.values(pendingSave.current).forEach(data => {
        oeSaveAnswer(attempt.id, data).catch(() => {});
      });
      pendingSave.current = {};
    }, 30000);
    return () => clearInterval(autoSaveRef.current);
  }, [attempt.id]);

  const selectAnswer = (question_id, option_id = null, text_answer = null) => {
    setAnswers(prev => ({ ...prev, [question_id]: { question_id, option_id, text_answer } }));
    pendingSave.current[question_id] = { question_id, option_id, text_answer };
    // Immediate save for MCQ
    if (option_id !== null) {
      oeSaveAnswer(attempt.id, { question_id, option_id, text_answer: null }).catch(() => {});
    }
  };

  const handleAutoSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const startedAt = new Date(attempt.started_at).getTime();
      const timeSpent = Math.floor((Date.now() - startedAt) / 1000);
      await oeSubmitExam(attempt.id, { time_spent_sec: timeSpent });
      onSubmitted(attempt.id);
    } catch { setSubmitting(false); }
  }, [attempt.id, submitting]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setShowConfirm(false);
    try {
      // Save any pending text answers
      for (const data of Object.values(pendingSave.current)) {
        await oeSaveAnswer(attempt.id, data).catch(() => {});
      }
      const startedAt = new Date(attempt.started_at).getTime();
      const timeSpent = Math.floor((Date.now() - startedAt) / 1000);
      await oeSubmitExam(attempt.id, { time_spent_sec: timeSpent });
      onSubmitted(attempt.id);
    } catch (err) {
      toast.error("Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  const answeredCount  = Object.keys(answers).length;
  const unansweredCount = allQuestions.length - answeredCount;
  const timerWarning   = timeLeft <= 300; // 5 min

  const getQStatus = (q) => {
    if (flagged[q.id]) return "flagged";
    if (answers[q.id]) return "answered";
    return "unanswered";
  };

  const statusColor = { answered: "#16a34a", unanswered: "#d1d5db", flagged: "#f59e0b" };

  const progressPct = allQuestions.length ? Math.round((answeredCount / allQuestions.length) * 100) : 0;
  const optionLetters = ["A", "B", "C", "D", "E", "F"];

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: "#f1f5f9" }}>

      {/* ── Left: Question Panel ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Top bar */}
        <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, flexShrink: 0, height: 56, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ flex: 1, fontWeight: 800, fontSize: 15, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {exam.name}
          </div>
          {/* Timer */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", borderRadius: 10,
            background: timerWarning ? "#fef2f2" : "#f0fdf4",
            border: `1.5px solid ${timerWarning ? "#fca5a5" : "#bbf7d0"}` }}>
            <Clock size={15} color={timerWarning ? "#dc2626" : "#16a34a"} />
            <span style={{ fontWeight: 800, fontSize: 17, color: timerWarning ? "#dc2626" : "#16a34a", fontVariantNumeric: "tabular-nums", letterSpacing: 1 }}>
              {fmtTime(timeLeft)}
            </span>
          </div>
          <button onClick={() => setShowConfirm(true)} disabled={submitting} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "9px 20px", borderRadius: 10,
            border: "none", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
            fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 8px #4f46e540",
          }}>
            {submitting ? <Loader size={13} className="ms2-spin" /> : <Send size={13} />} Submit Exam
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: "#e5e7eb", flexShrink: 0 }}>
          <div style={{ height: "100%", width: `${progressPct}%`, background: "linear-gradient(90deg,#4f46e5,#7c3aed)", transition: "width 0.4s" }} />
        </div>

        {/* Question area */}
        <div style={{ flex: 1, overflow: "auto", padding: "28px 32px" }}>
          {currentQ && (
            <>
              {/* Question Card */}
              <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: 20 }}>
                {/* Card header */}
                <div style={{ background: "linear-gradient(135deg,#4f46e508,#7c3aed08)", borderBottom: "1px solid #e5e7eb", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {/* Q number badge */}
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 6px #4f46e540" }}>
                      <span style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>Q{currentIdx + 1}</span>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: 1 }}>
                        SECTION {currentQ.section_label} · {currentIdx + 1} of {allQuestions.length}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                        {currentQ.question_type?.replace(/_/g, " ").toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {currentQ.marks && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", background: "#ede9fe", padding: "4px 12px", borderRadius: 20, border: "1px solid #c4b5fd" }}>
                        {currentQ.marks} {parseFloat(currentQ.marks) === 1 ? "Mark" : "Marks"}
                      </span>
                    )}
                    <button onClick={() => setFlagged(p => ({ ...p, [currentQ.id]: !p[currentQ.id] }))} style={{
                      display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8,
                      border: `1.5px solid ${flagged[currentQ.id] ? "#f59e0b" : "#e5e7eb"}`,
                      background: flagged[currentQ.id] ? "#fffbeb" : "#fff",
                      color: flagged[currentQ.id] ? "#d97706" : "#9ca3af",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}>
                      <Flag size={12} /> {flagged[currentQ.id] ? "Flagged" : "Flag"}
                    </button>
                  </div>
                </div>

                {/* Question text */}
                <div style={{ padding: "20px 24px 0" }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#111827", lineHeight: 1.7 }}>
                    {currentQ.question_type === "match_following" ? (() => {
                      try { const p = JSON.parse(currentQ.question_text || "{}"); return p.instruction || "Match the Following"; }
                      catch { return currentQ.question_text; }
                    })() : currentQ.question_text}
                  </div>
                  {currentQ.image_url && (
                    <img src={currentQ.image_url} alt="question" style={{ maxWidth: "100%", borderRadius: 10, marginTop: 14, border: "1px solid #e5e7eb" }} />
                  )}
                </div>

                {/* Answer area */}
                <div style={{ padding: "20px 24px 24px" }}>

                  {/* MCQ / True-False */}
                  {["mcq", "true_false"].includes(currentQ.question_type) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {(currentQ.options || []).map((opt, oi) => {
                        const selected = answers[currentQ.id]?.option_id === opt.id;
                        return (
                          <button key={opt.id} onClick={() => selectAnswer(currentQ.id, opt.id)} style={{
                            display: "flex", alignItems: "center", gap: 14, padding: "13px 18px",
                            borderRadius: 12, cursor: "pointer", textAlign: "left", width: "100%",
                            border: `2px solid ${selected ? "#4f46e5" : "#e5e7eb"}`,
                            background: selected ? "linear-gradient(135deg,#ede9fe,#f5f3ff)" : "#fafafa",
                            boxShadow: selected ? "0 2px 8px #4f46e520" : "none",
                            transition: "all 0.15s",
                          }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                              background: selected ? "#4f46e5" : "#fff",
                              border: `2px solid ${selected ? "#4f46e5" : "#d1d5db"}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12, fontWeight: 800, color: selected ? "#fff" : "#6b7280",
                            }}>
                              {optionLetters[oi]}
                            </div>
                            <span style={{ fontSize: 14, color: selected ? "#3730a3" : "#111827", fontWeight: selected ? 600 : 400 }}>
                              {opt.option_text}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Fill blank / Short answer */}
                  {["short", "short_answer", "fill_blank"].includes(currentQ.question_type) && (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 8 }}>
                        {currentQ.question_type === "fill_blank" ? "Fill in the blank:" : "Your Answer:"}
                      </label>
                      <input
                        type="text"
                        value={answers[currentQ.id]?.text_answer || ""}
                        onChange={e => selectAnswer(currentQ.id, null, e.target.value)}
                        onBlur={e => oeSaveAnswer(attempt.id, { question_id: currentQ.id, option_id: null, text_answer: e.target.value }).catch(() => {})}
                        placeholder={currentQ.question_type === "fill_blank" ? "Type your answer here..." : "Type your answer here..."}
                        style={{ width: "100%", padding: "13px 16px", borderRadius: 10, border: "2px solid #e5e7eb", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", background: "#fafafa", transition: "border 0.15s" }}
                        onFocus={e => e.target.style.borderColor = "#4f46e5"}
                      />
                    </div>
                  )}

                  {/* Match the Following */}
                  {currentQ.question_type === "match_following" && (() => {
                    let colA = [], colB = [];
                    try {
                      const p = JSON.parse(currentQ.question_text || "{}");
                      colA = Array.isArray(p.colA) ? p.colA : [];
                      colB = Array.isArray(p.colB) ? p.colB : [];
                    } catch {}
                    const colBItems = colB.map((text, i) => ({ letter: String.fromCharCode(97 + i), text }));
                    const savedText = answers[currentQ.id]?.text_answer || "";
                    const currentSel = {};
                    savedText.split(",").forEach(part => {
                      const m = part.trim().match(/^(\d+)[–\-]([a-z])$/i);
                      if (m) currentSel[Number(m[1])] = m[2].toLowerCase();
                    });
                    const handleMatchChange = (num, letter) => {
                      const next = { ...currentSel, [num]: letter };
                      const text = Object.entries(next).sort(([a],[b]) => Number(a)-Number(b)).map(([n,l]) => `${n}-${l}`).join(", ");
                      selectAnswer(currentQ.id, null, text);
                      oeSaveAnswer(attempt.id, { question_id: currentQ.id, option_id: null, text_answer: text }).catch(() => {});
                    };
                    if (!colA.length) return <div style={{ color: "#9ca3af", fontSize: 13 }}>No pairs defined.</div>;
                    return (
                      <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
                        {/* Column B options */}
                        <div style={{ background: "#f0f9ff", borderBottom: "1px solid #bae6fd", padding: "12px 20px" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", marginBottom: 8, letterSpacing: 1 }}>COLUMN B — Options</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            {colBItems.map(b => (
                              <span key={b.letter} style={{ fontSize: 13, color: "#0369a1", background: "#e0f2fe", padding: "4px 14px", borderRadius: 20, fontWeight: 500 }}>
                                <strong style={{ marginRight: 4 }}>{b.letter}.</strong>{b.text}
                              </span>
                            ))}
                          </div>
                        </div>
                        {/* Column A rows */}
                        {colA.map((label, i) => {
                          const num = i + 1;
                          const sel = currentSel[num];
                          return (
                            <div key={num} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 20px", borderBottom: i < colA.length - 1 ? "1px solid #f3f4f6" : "none", background: "#fff" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 140 }}>
                                <span style={{ width: 26, height: 26, borderRadius: 7, background: "#4f46e5", color: "#fff", fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{num}</span>
                                <span style={{ fontSize: 14, color: "#111827", fontWeight: 500 }}>{label}</span>
                              </div>
                              <span style={{ color: "#c4b5fd", fontSize: 18 }}>→</span>
                              <select
                                value={sel || ""}
                                onChange={e => handleMatchChange(num, e.target.value)}
                                style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `2px solid ${sel ? "#4f46e5" : "#e5e7eb"}`, background: sel ? "#ede9fe" : "#fafafa", fontSize: 13, color: "#111827", cursor: "pointer", outline: "none", fontWeight: sel ? 600 : 400 }}
                              >
                                <option value="">— Select match —</option>
                                {colBItems.map(b => (
                                  <option key={b.letter} value={b.letter}>{b.letter}. {b.text}</option>
                                ))}
                              </select>
                              {sel && <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 700, flexShrink: 0 }}>✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Long / descriptive */}
                  {["long", "long_answer", "descriptive", "subjective"].includes(currentQ.question_type) && (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 8 }}>Your Answer:</label>
                      <textarea
                        value={answers[currentQ.id]?.text_answer || ""}
                        onChange={e => selectAnswer(currentQ.id, null, e.target.value)}
                        onBlur={e => oeSaveAnswer(attempt.id, { question_id: currentQ.id, option_id: null, text_answer: e.target.value }).catch(() => {})}
                        placeholder="Write your detailed answer here..."
                        rows={7}
                        style={{ width: "100%", padding: "13px 16px", borderRadius: 10, border: "2px solid #e5e7eb", fontSize: 14, color: "#111827", resize: "vertical", outline: "none", lineHeight: 1.7, boxSizing: "border-box", background: "#fafafa" }}
                        onFocus={e => e.target.style.borderColor = "#4f46e5"}
                      />
                    </div>
                  )}

                </div>
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <div style={{ background: "#fff", borderTop: "1px solid #e5e7eb", padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 10,
            border: "1.5px solid #e5e7eb", background: currentIdx === 0 ? "#f9fafb" : "#fff",
            color: currentIdx === 0 ? "#d1d5db" : "#374151",
            fontSize: 13, fontWeight: 600, cursor: currentIdx === 0 ? "not-allowed" : "pointer",
          }}>
            <ChevronLeft size={16} /> Previous
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />
              <strong style={{ color: "#16a34a" }}>{answeredCount}</strong>
              <span style={{ color: "#9ca3af" }}>answered</span>
            </span>
            <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#e5e7eb", display: "inline-block" }} />
              <strong style={{ color: "#6b7280" }}>{unansweredCount}</strong>
              <span style={{ color: "#9ca3af" }}>remaining</span>
            </span>
          </div>
          <button onClick={() => setCurrentIdx(i => Math.min(allQuestions.length - 1, i + 1))} disabled={currentIdx === allQuestions.length - 1} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 10,
            border: "1.5px solid #e5e7eb", background: currentIdx === allQuestions.length - 1 ? "#f9fafb" : "#4f46e5",
            color: currentIdx === allQuestions.length - 1 ? "#d1d5db" : "#fff",
            fontSize: 13, fontWeight: 600, cursor: currentIdx === allQuestions.length - 1 ? "not-allowed" : "pointer",
          }}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* ── Right: Question Navigator ── */}
      <div style={{ width: 230, background: "#fff", borderLeft: "1px solid #e5e7eb", display: "flex", flexDirection: "column", overflow: "hidden", flexShrink: 0, boxShadow: "-2px 0 8px rgba(0,0,0,0.04)" }}>
        {/* Navigator header */}
        <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#111827", marginBottom: 10 }}>Question Navigator</div>
          {/* Progress */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 11, color: "#6b7280" }}>
            <span>{progressPct}% completed</span>
            <span>{answeredCount}/{allQuestions.length}</span>
          </div>
          <div style={{ height: 5, background: "#e5e7eb", borderRadius: 10 }}>
            <div style={{ height: "100%", width: `${progressPct}%`, background: "linear-gradient(90deg,#4f46e5,#7c3aed)", borderRadius: 10, transition: "width 0.4s" }} />
          </div>
        </div>
        {/* Legend */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 5 }}>
          {[["#16a34a", "#dcfce7", "Answered"], ["#9ca3af", "#f3f4f6", "Not Answered"], ["#d97706", "#fef9c3", "Flagged"]].map(([c, bg, l]) => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: "#6b7280" }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, background: bg, border: `1.5px solid ${c}` }} />
              {l}
            </div>
          ))}
        </div>
        {/* Grid */}
        <div style={{ padding: "12px", overflow: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 7 }}>
            {allQuestions.map((q, i) => {
              const st = getQStatus(q);
              const isActive = i === currentIdx;
              const bgMap = { answered: "#dcfce7", unanswered: "#f9fafb", flagged: "#fef9c3" };
              const colorMap = { answered: "#16a34a", unanswered: "#9ca3af", flagged: "#d97706" };
              const borderMap = { answered: "#16a34a", unanswered: "#e5e7eb", flagged: "#d97706" };
              return (
                <button key={q.id} onClick={() => setCurrentIdx(i)} style={{
                  width: "100%", aspectRatio: "1", borderRadius: 9,
                  border: `2px solid ${isActive ? "#4f46e5" : borderMap[st]}`,
                  background: isActive ? "#4f46e5" : bgMap[st],
                  color: isActive ? "#fff" : colorMap[st],
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  boxShadow: isActive ? "0 2px 8px #4f46e540" : "none",
                  transition: "all 0.1s",
                }}>
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Confirm Submit Modal ── */}
      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "32px 28px", width: 400, maxWidth: "92vw", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <AlertTriangle size={40} color="#f59e0b" style={{ margin: "0 auto 16px" }} />
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Submit Exam?</div>
            <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 6 }}>
              You have answered <strong>{answeredCount}</strong> of <strong>{allQuestions.length}</strong> questions.
            </div>
            {unansweredCount > 0 && (
              <div style={{ fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
                {unansweredCount} question{unansweredCount > 1 ? "s" : ""} unanswered.
              </div>
            )}
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>
              Once submitted, you cannot change your answers.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setShowConfirm(false)} style={{ padding: "10px 24px", borderRadius: 9, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Continue Exam
              </button>
              <button onClick={handleSubmit} style={{ padding: "10px 24px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#4f46e5,#a855f7)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── RESULT VIEW ─── */
function ResultView({ attemptId, onBack }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    oeGetResult(attemptId)
      .then(r => setData(r.data.data))
      .catch(() => toast.error("Failed to load result"))
      .finally(() => setLoading(false));
  }, [attemptId]);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
      <Loader size={32} className="ms2-spin" color="#4f46e5" />
    </div>
  );

  if (!data) return null;

  if (!data.result_visible) return (
    <div style={{ maxWidth: 500, margin: "80px auto", textAlign: "center", padding: "0 16px" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "48px 32px", border: "1.5px solid #e5e7eb" }}>
        <Eye size={48} color="#e5e7eb" style={{ margin: "0 auto 20px" }} />
        <div style={{ fontWeight: 800, fontSize: 18, color: "#374151", marginBottom: 8 }}>Results Not Yet Published</div>
        <div style={{ fontSize: 14, color: "#9ca3af" }}>Your teacher will publish results soon.</div>
        <div style={{ marginTop: 20 }}>
          <StatusBadge status={data.attempt.status} />
        </div>
        <button onClick={onBack} style={{ marginTop: 24, padding: "10px 28px", borderRadius: 9, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
          Back to Exams
        </button>
      </div>
    </div>
  );

  const { attempt, answers } = data;
  const passFail = attempt.pass_fail;
  const isPass   = passFail === "pass";
  const isPending = passFail === "pending";

  const totalAnswered = answers?.length || 0;
  const totalCorrect  = answers?.filter(a => a.is_correct === 1).length || 0;
  const totalWrong    = answers?.filter(a => a.is_correct === 0).length || 0;
  const totalPending  = answers?.filter(a => a.is_correct === null).length || 0;

  const getQText = (ans) => {
    if (ans.question_type === "match_following") {
      try { return JSON.parse(ans.question_text || "{}").instruction || "Match the Following"; }
      catch { return ans.question_text; }
    }
    return ans.question_text;
  };

  const getStudentAnswerText = (ans) => {
    if (ans.question_type === "match_following") {
      try {
        const p = JSON.parse(ans.question_text || "{}");
        const colA = Array.isArray(p.colA) ? p.colA : [];
        const colB = Array.isArray(p.colB) ? p.colB : [];
        return (ans.text_answer || "").split(",").map(part => {
          const m = part.trim().match(/^(\d+)[–\-]([a-z])/i);
          if (!m) return null;
          const idx = Number(m[1]) - 1;
          const letter = m[2].toLowerCase();
          const bIdx = letter.charCodeAt(0) - 97;
          return `${colA[idx] || idx + 1} → ${colB[bIdx] || letter}`;
        }).filter(Boolean).join(", ");
      } catch {}
    }
    if (ans.selected_option_text) {
      // avoid "A. A" duplication — show label only if different from text
      const label = ans.selected_option_label || "";
      const text  = ans.selected_option_text || "";
      if (label && text && label.toLowerCase() !== text.toLowerCase()) {
        return `${label}. ${text}`;
      }
      return text;
    }
    return ans.text_answer || "";
  };

  return (
    <div style={{ padding: "0 0 40px" }}>

      {/* Score card */}
      <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.06)", border: "1px solid #e5e7eb" }}>
        <div style={{ height: 4, background: isPending ? "linear-gradient(90deg,#f59e0b,#fbbf24)" : isPass ? "linear-gradient(90deg,#16a34a,#4ade80)" : "linear-gradient(90deg,#dc2626,#f87171)" }} />
        {/* Single compact row: score | divider | stats */}
        <div style={{ display: "flex", alignItems: "center", padding: "16px 24px", gap: 0 }}>
          {/* Score + badge */}
          <div style={{ textAlign: "center", paddingRight: 24, borderRight: "1px solid #f3f4f6", minWidth: 160 }}>
            <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: isPending ? "#92400e" : isPass ? "#16a34a" : "#dc2626" }}>
              {attempt.score}
              <span style={{ fontSize: 16, fontWeight: 500, color: "#9ca3af" }}> / {attempt.total_marks}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", marginTop: 2 }}>{attempt.percentage}%</div>
            <span style={{
              display: "inline-block", marginTop: 6, fontSize: 11, fontWeight: 800, padding: "3px 14px", borderRadius: 20,
              background: isPending ? "#fef9c3" : isPass ? "#dcfce7" : "#fee2e2",
              color: isPending ? "#92400e" : isPass ? "#166534" : "#991b1b",
              letterSpacing: 0.8,
            }}>
              {isPending ? "PENDING" : passFail?.toUpperCase()}
            </span>
          </div>
          {/* Stats */}
          <div style={{ flex: 1, display: "flex" }}>
            {[
              { label: "Questions", value: totalAnswered, color: "#374151" },
              { label: "Correct",   value: totalCorrect,  color: "#16a34a" },
              { label: "Wrong",     value: totalWrong,    color: "#dc2626" },
              { label: "Pending",   value: totalPending,  color: "#f59e0b" },
              { label: "Time Spent", value: attempt.time_spent_sec ? `${Math.floor(attempt.time_spent_sec / 60)}m ${attempt.time_spent_sec % 60}s` : "—", color: "#4f46e5" },
            ].map(({ label, value, color }, idx, arr) => (
              <div key={label} style={{ flex: 1, textAlign: "center", borderRight: idx < arr.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600, marginTop: 3 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Answer Review */}
      {answers && answers.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: 20 }}>
          {/* Section header */}
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Answer Review</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { label: "Correct", count: totalCorrect, bg: "#dcfce7", color: "#166534" },
                { label: "Wrong",   count: totalWrong,   bg: "#fee2e2", color: "#991b1b" },
                { label: "Pending", count: totalPending, bg: "#fef9c3", color: "#92400e" },
              ].filter(s => s.count > 0).map(s => (
                <span key={s.label} style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color }}>
                  {s.count} {s.label}
                </span>
              ))}
            </div>
          </div>

          <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
            {answers.map((ans, i) => {
              const correct  = ans.is_correct === 1;
              const wrong    = ans.is_correct === 0;
              const pending  = ans.is_correct === null;
              const qType    = ans.question_type;
              const isAutoGraded = ["mcq", "true_false", "fill_blank", "match_following"].includes(qType);
              const studentAns   = getStudentAnswerText(ans);

              // Build correct answer text for auto-graded types
              const getCorrectAnswerDisplay = () => {
                if (qType === "match_following") {
                  try {
                    const p = JSON.parse(ans.question_text || "{}");
                    const colA = Array.isArray(p.colA) ? p.colA : [];
                    const colB = Array.isArray(p.colB) ? p.colB : [];
                    const keys = Array.isArray(p.answers) ? p.answers : [];
                    return keys.map((letter, idx) => {
                      const bIdx = letter.charCodeAt(0) - 97;
                      return `${colA[idx] || idx + 1} → ${colB[bIdx] || letter}`;
                    }).join(", ");
                  } catch { return null; }
                }
                if (["mcq", "true_false"].includes(qType) && ans.correct_option_text) {
                  const label = ans.correct_option_label || "";
                  const text  = ans.correct_option_text || "";
                  return label && label.toLowerCase() !== text.toLowerCase() ? `${label}. ${text}` : text;
                }
                if (qType === "fill_blank" && ans.q_correct_text) return ans.q_correct_text;
                return null;
              };

              const correctAnswerDisplay = isAutoGraded ? getCorrectAnswerDisplay() : null;

              return (
                <div key={ans.id} style={{
                  borderRadius: 12, overflow: "hidden",
                  border: `1.5px solid ${correct ? "#bbf7d0" : wrong ? "#fecaca" : "#e5e7eb"}`,
                }}>
                  {/* Question header */}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 16px",
                    background: correct ? "#f0fdf4" : wrong ? "#fef2f2" : "#f8fafc",
                    borderBottom: `1px solid ${correct ? "#bbf7d0" : wrong ? "#fecaca" : "#e5e7eb"}`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: correct ? "#16a34a" : wrong ? "#dc2626" : "#e5e7eb",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 800,
                        color: pending ? "#6b7280" : "#fff",
                      }}>
                        Q{i + 1}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                        {ans.section_label && `Section ${ans.section_label} · `}
                        {qType?.replace(/_/g, " ").toUpperCase()}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {ans.marks_awarded != null && (
                        <span style={{ fontSize: 12, fontWeight: 700,
                          color: correct ? "#166534" : wrong ? "#991b1b" : "#92400e" }}>
                          {ans.marks_awarded} / {ans.question_marks} marks
                        </span>
                      )}
                      <span style={{
                        fontSize: 11, fontWeight: 800, padding: "2px 10px", borderRadius: 20,
                        background: correct ? "#dcfce7" : wrong ? "#fee2e2" : "#fef9c3",
                        color: correct ? "#166534" : wrong ? "#991b1b" : "#92400e",
                      }}>
                        {correct ? "✓ Correct" : wrong ? "✗ Wrong" : "⏳ Pending"}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div style={{ padding: "14px 16px", background: "#fff" }}>
                    {/* Question text */}
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.7, marginBottom: 10 }}>
                      {getQText(ans)}
                    </div>

                    {/* Answer comparison row for auto-graded */}
                    {isAutoGraded && (
                      <div style={{ display: "grid", gridTemplateColumns: studentAns ? "1fr 1fr" : "1fr", gap: 8, marginBottom: 6 }}>
                        {/* Student answer */}
                        {studentAns && (
                          <div style={{
                            background: correct ? "#f0fdf4" : wrong ? "#fef2f2" : "#f8fafc",
                            borderRadius: 8, padding: "8px 12px",
                            border: `1px solid ${correct ? "#bbf7d0" : wrong ? "#fecaca" : "#e5e7eb"}`,
                          }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", marginBottom: 4, letterSpacing: 0.8 }}>YOUR ANSWER</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: correct ? "#166534" : wrong ? "#991b1b" : "#374151" }}>
                              {studentAns}
                            </div>
                          </div>
                        )}
                        {/* Correct answer — always shown for auto-graded */}
                        {correctAnswerDisplay && (
                          <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "8px 12px", border: "1px solid #bbf7d0" }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: "#166534", marginBottom: 4, letterSpacing: 0.8 }}>CORRECT ANSWER</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
                              {correctAnswerDisplay}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* For manual/subjective — just show student answer */}
                    {!isAutoGraded && studentAns && (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6, fontSize: 13 }}>
                        <span style={{ color: "#9ca3af", fontWeight: 600, flexShrink: 0 }}>Your answer:</span>
                        <span style={{ fontWeight: 700, color: "#374151" }}>{studentAns}</span>
                      </div>
                    )}

                    {/* Pending note */}
                    {pending && (
                      <div style={{ fontSize: 12, color: "#92400e", background: "#fef9c3", padding: "6px 10px", borderRadius: 6, display: "inline-block" }}>
                        Awaiting teacher review
                      </div>
                    )}

                    {/* Explanation */}
                    {ans.explanation && (
                      <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280", background: "#f8fafc", borderRadius: 8, padding: "8px 12px", borderLeft: "3px solid #4f46e5" }}>
                        <strong style={{ color: "#4f46e5" }}>Explanation:</strong> {ans.explanation}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        <button onClick={onBack} style={{ padding: "12px 36px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 2px 10px #4f46e540" }}>
          ← Back to Exams
        </button>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function StudentOnlineExam() {
  const { examId } = useParams();
  const nav = useNavigate();
  const userData  = getStudentData();
  const studentId = userData?.admin_id || null;
  const schoolId  = userData?.school_id || userData?.admin_id || null;

  const [phase,    setPhase]    = useState("loading"); // loading | lobby | player | result
  const [info,     setInfo]     = useState(null);
  const [session,  setSession]  = useState(null); // { attempt, exam, paper, saved_answers }
  const [attemptId, setAttemptId] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    oeGetExamInfo(examId, { student_id: studentId })
      .then(r => {
        setInfo(r.data.data);
        const att = r.data.data?.attempt;
        if (att?.status === "submitted" || att?.status === "graded") {
          setAttemptId(att.id);
          setPhase("result");
        } else {
          setPhase("lobby");
        }
      })
      .catch(() => { toast.error("Failed to load exam"); nav(-1); });
  }, [examId]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const r = await oeStartExam(examId, { student_id: studentId, school_id: schoolId });
      setSession(r.data.data);
      setPhase("player");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start exam");
      setStarting(false);
    }
  };

  const handleSubmitted = (attId) => {
    setAttemptId(attId);
    setPhase("result");
  };

  if (phase === "loading") return (
    <div style={{ display: "flex", justifyContent: "center", padding: "100px 0" }}>
      <Loader size={32} className="ms2-spin" color="#4f46e5" />
    </div>
  );

  if (phase === "lobby") return (
    <div className="lms-content-inner">
      <ExamLobby info={info} onStart={handleStart} starting={starting} />
    </div>
  );

  if (phase === "player" && session) return (
    <ExamPlayer
      attempt={session.attempt}
      exam={session.exam}
      paper={session.paper}
      savedAnswers={session.saved_answers}
      onSubmitted={handleSubmitted}
    />
  );

  if (phase === "result") return (
    <div className="lms-content-inner">
      <ResultView attemptId={attemptId} onBack={() => nav("/student-lms/exams")} />
    </div>
  );

  return null;
}
